import { Amplify, Auth, Hub } from "aws-amplify";
import { siteSettings } from "core/siteSettings";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useState
} from "react";

// Setup auth configuration
Amplify.configure({
  Auth: {
    region: siteSettings.cognito.region,
    userPoolId: siteSettings.cognito.userPoolId,
    userPoolWebClientId: siteSettings.cognito.userPoolWebClientId,
    mandatorySignIn: true,
    oauth: {
      domain: siteSettings.cognito.domain,
      scope: ["email", "openid", "profile"],
      redirectSignIn: siteSettings.cognito.redirectSignIn,
      redirectSignOut: siteSettings.cognito.redirectSignOut,
      responseType: "code"
    }
  }
});

// User
class CognitoUser {
  userName?: string;
  sub?: string;
  position?: string;
  onyxUser?: boolean;

  email?: string;
  givenName?: string;
  familyName?: string;

  get fullName(): string | undefined {
    return `${this.givenName ?? ""} ${this.familyName ?? ""}`;
  }
}

export enum ICognitoAuthContextState {
  Initialisation,
  LoggedIn,
  TokenRefreshFailed,
  Errored,
  LoggedOut
}

// Context interface
interface CognitoAuthContextType {
  state: ICognitoAuthContextState;
  user?: CognitoUser;

  error?: any;
  setError: (err: string) => void;
  login: () => void;
  logout: () => void;
  getToken: () => Promise<string>;
}

// Create the react context (with an initial dummy instance)
export const CognitoAuthContext = createContext<CognitoAuthContextType>({
  state: ICognitoAuthContextState.Initialisation,
  user: undefined,
  error: "",
  setError: () => {
    console.error("Context used outside of provider.");
  },
  login: () => {
    console.error("Context used outside of provider.");
  },
  logout: () => {
    console.error("Context used outside of provider.");
  },
  getToken: async () => {
    console.error("Context used outside of provider.");
    return "";
  }
});
CognitoAuthContext.displayName = "CognitoAuthContext";

// Export the provider as we need to wrap the entire app with it
export function CognitoAuthProvider({
  children
}: {
  children: ReactNode;
}): JSX.Element {
  const [state, setState] = useState<ICognitoAuthContextState>(
    ICognitoAuthContextState.Initialisation
  );
  const [user, setUser] = useState<CognitoUser>();
  const [error, setError] = useState<any>("");

  // Output login error to the console
  useEffect(() => {
    if (error) console.error("Login error: ", error);
  }, [error]);

  // Run once use effect to setup the auth hub listener and check the user is logged in
  useEffect(() => {
    // Change the state based on log in / out events
    // ref https://docs.amplify.aws/lib/auth/auth-events/q/platform/js/
    // For more verbose logging add this after the auth config
    // (window as any).LOG_LEVEL = 'VERBOSE';
    const hubListenerCancelToken = Hub.listen(
      "auth",
      ({ payload: { event, data, message } }) => {
        // console.log(`Auth Hub event [${event}]: ${message}: `, data);

        switch (event) {
          case "tokenRefresh_failure":
            const tokenErrorMessage = message + " " + data;
            setUser(undefined);
            setError(tokenErrorMessage);
            setState(ICognitoAuthContextState.TokenRefreshFailed);
            break;
          case "signIn":
            // We do not need this auth event as we are using the HostedUI which will fire after this
            break;
          case "cognitoHostedUI":
            var idToken = data?.signInUserSession?.idToken?.payload;
            var newUser = createUserFromToken(idToken);
            setUser(newUser);
            setError("");
            setState(ICognitoAuthContextState.LoggedIn);
            break;
          case "signIn_failure":
          case "autoSignIn_failure":
          case "cognitoHostedUI_failure":
          case "customState_failure":
            // Note this could be triggered multiple times. E.g. a log in failure may fire all 4.
            const errorMessage = message + " " + data;
            setUser(undefined);
            setError(errorMessage);
            setState(ICognitoAuthContextState.Errored);
            break;
          case "signOut":
            setUser(undefined);
            setError("");
            setState(ICognitoAuthContextState.LoggedOut);
            break;
        }
      }
    );

    // We need to stop listening to events when the context unmounts
    return () => hubListenerCancelToken();
  }, []);

  useEffect(() => {
    // Check the current authenticated status
    Auth.currentAuthenticatedUser()
      .then(sessionInfo => {
        var idToken = sessionInfo?.signInUserSession?.idToken?.payload;
        var newUser = createUserFromToken(idToken);
        setUser(newUser);
      })
      .catch(_error => {
        // Do not set the error state here. It will conflict with the Auth.Hub error var setting
        // If no user has logged in or the session has expired it is expected for this to error

        setState(ICognitoAuthContextState.LoggedOut);
      });
  }, []);

  function createUserFromToken(idToken: any): CognitoUser {
    var user = new CognitoUser();
    user.userName = idToken["cognito:username"];
    user.email = idToken.email;
    user.sub = idToken.sub;
    user.givenName = idToken.given_name;
    user.familyName = idToken.family_name;
    user.onyxUser = idToken["custom:onyx_user"] === "1";

    return user;
  }

  // Call the login endpoint. The Hub.listen will setup the user state.
  const login = useCallback((): void => {
    Auth.federatedSignIn();
  }, []);

  // Call the logout endpoint. The Hub.listen will remove the user from the state.
  const logout = useCallback((): void => {
    setUser(undefined);
    setError("");
    Auth.signOut();
  }, []);

  const getToken = useCallback(async (): Promise<string> => {
    let result = await Auth.currentSession();
    return result.getAccessToken().getJwtToken();
  }, []);

  // We only want to render the underlying app after we assert for the presence of a current user.
  return (
    <CognitoAuthContext.Provider
      value={{
        state,
        user,
        error,
        setError,
        login,
        logout,
        getToken
      }}
    >
      {children}
    </CognitoAuthContext.Provider>
  );
}
